import React, { useMemo, useState, type FC } from 'react'

import { useIntl } from 'react-intl'

import { useRecoilCallback, useRecoilValue } from 'recoil'

import { Flex, FormElement, Select } from 'mailup-web-components'

import { INPUT_MESSAGES_MAX_ITEMS } from 'Constants'

import GroupsList from 'Pages/RecipientsList/components/insert/GroupsList'

import useAsyncSelect from 'Hooks/useAsyncSelect/useAsyncSelect'

import {
  panelAddRecipientAddGroupCallback,
  panelAddRecipientNewGroupCallback,
} from 'Pages/RecipientsList/recoil/callbacks/addRecipient'
import {
  fetchGroupById,
  fetchGroups,
} from 'Pages/RecipientsList/recoil/services/asyncSelectGroups'
import { panelAddRecipientState } from 'Pages/RecipientsList/recoil/state/addRecipient'

const BoxAddGroups: FC = () => {
  const intl = useIntl()

  const [forceCollapse, setForceCollapse] = useState(false)

  const {
    recipient: { Groups },
  } = useRecoilValue(panelAddRecipientState)

  const handleSelectGroupChange = useRecoilCallback(
    panelAddRecipientAddGroupCallback,
    [],
  )
  const addNewGroup = useRecoilCallback(panelAddRecipientNewGroupCallback, [])

  const { options, loading, handleSearch, hint, hintNoResults, updateOptions } =
    useAsyncSelect({
      key: 'recipient-add__groups',
      value: undefined,
      maxItems: INPUT_MESSAGES_MAX_ITEMS,
      fetchItems: async () => await fetchGroups(''),
      fetchItemsByTerm: fetchGroups,
      fetchItemById: fetchGroupById,
      labels: {
        loading: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusLoading',
        }),
        noResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusNoMatches',
        }),
        moreResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusTooManyItems',
        }),
      },
    })

  const handleSelectGroupExpandCollapse = (c: boolean): void => {
    if (!c) {
      handleSearch('')
    }
  }

  const handleSelectGroupSubmit = async (s: string): Promise<void> => {
    const newGroupId = await addNewGroup(s)

    if (newGroupId !== undefined) {
      void updateOptions(newGroupId)
    }

    setForceCollapse(true)

    setTimeout(() => {
      setForceCollapse(false)
    }, 250)
  }

  const decoratedOptions = useMemo(() => {
    return options.map((o: { value: number }) => ({
      ...o,
      disabled: Groups.includes(o.value),
    }))
  }, [options, Groups])

  return (
    <Flex flexDirection="column" gap="24px" margin="24px 0 0 0">
      <FormElement
        label={intl.formatMessage({
          id: 'Load.RecipientsList.AddToGroupsLabel',
        })}
        hint={
          <span>
            {intl.formatMessage(
              { id: 'Load.RecipientsList.AddToGroupsHint' },
              { strong: (chunks) => <strong>{chunks}</strong> },
            )}
          </span>
        }
      >
        <Select
          value={undefined}
          options={decoratedOptions}
          loading={loading}
          onChange={handleSelectGroupChange}
          onSearch={handleSearch}
          onSubmit={handleSelectGroupSubmit}
          onExpandCollapse={handleSelectGroupExpandCollapse}
          hint={hint}
          hintNoResults={hintNoResults}
          memoSelectOption
          placeholder={intl.formatMessage({
            id: 'Load.RecipientsList.SelectOrAddGroupPlaceholder',
          })}
          forceCollapse={forceCollapse}
          data-mu-test="recipient-add__input-select-group-to-add"
        />
      </FormElement>

      <GroupsList />
    </Flex>
  )
}

export default BoxAddGroups
